import { Link } from "react-router-dom";
import { Youtube, Instagram, Facebook, Mail, Music2, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const Footer = () => {
  const { t } = useLang();

  return (
    <footer data-testid="site-footer" className="bg-[#0E0D0C] text-[#F7F5F0] border-t border-[#C5A059]/20">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <img src="/assets/logo-white.webp" alt="Monferrato Immersive®" className="h-12 w-auto" data-testid="footer-logo" />
            <p className="font-serif-display italic text-lg text-[#F7F5F0]/70 mt-6 max-w-xs">
              {t("footer.tagline")}
            </p>
            <a
              href="mailto:monferratoimmersive@gmail.com"
              data-testid="footer-email"
              className="mt-6 inline-flex items-center gap-3 text-sm text-[#F7F5F0]/80 hover:text-[#C5A059] transition-colors"
            >
              <Mail size={15} className="text-[#C5A059]" />
              monferratoimmersive@gmail.com
            </a>
            <div className="flex gap-5 mt-6">
              <a href="https://www.youtube.com/@MonferratoImmersive" target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="footer-social-youtube" className="text-[#F7F5F0]/60 hover:text-[#C5A059] transition-colors"><Youtube size={18} /></a>
              <a href="https://www.instagram.com/monferratoimmersive" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-social-instagram" className="text-[#F7F5F0]/60 hover:text-[#C5A059] transition-colors"><Instagram size={18} /></a>
              <a href="https://www.facebook.com/monferratoimmersive" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-social-facebook" className="text-[#F7F5F0]/60 hover:text-[#C5A059] transition-colors"><Facebook size={18} /></a>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-[11px] tracking-[0.25em] uppercase text-[#C5A059] font-medium mb-5">{t("footer.explore")}</h3>
            <ul className="space-y-3 text-sm text-[#F7F5F0]/70">
              <li><Link to="/#esplora" className="hover:text-[#C5A059] transition-colors" data-testid="footer-link-borghi">{t("footer.borghi")}</Link></li>
              <li><Link to="/esperienze" className="hover:text-[#C5A059] transition-colors" data-testid="footer-link-esperienze">{t("footer.esperienze")}</Link></li>
              <li><Link to="/#mappa" className="hover:text-[#C5A059] transition-colors" data-testid="footer-link-mappa">{t("footer.mappa")}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="text-[11px] tracking-[0.25em] uppercase text-[#C5A059] font-medium mb-5">{t("footer.progetto")}</h3>
            <ul className="space-y-3 text-sm text-[#F7F5F0]/70">
              <li><Link to="/il-progetto" className="hover:text-[#C5A059] transition-colors" data-testid="footer-link-progetto">{t("footer.visione")}</Link></li>
              <li><Link to="/eventi" className="hover:text-[#C5A059] transition-colors" data-testid="footer-link-eventi">{t("footer.eventi")}</Link></li>
              <li><Link to="/per-comuni-e-partner" className="hover:text-[#C5A059] transition-colors" data-testid="footer-link-partner">{t("footer.partner")}</Link></li>
              <li><Link to="/chi-sono" className="hover:text-[#C5A059] transition-colors" data-testid="footer-link-chi-sono">{t("footer.chisono")}</Link></li>
              <li><Link to="/contatti" className="hover:text-[#C5A059] transition-colors" data-testid="footer-link-contatti">{t("footer.contatti")}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="text-[11px] tracking-[0.25em] uppercase text-[#C5A059] font-medium mb-5 flex items-center gap-2">
              <Music2 size={13} /> Echoes From Above
            </h3>
            <p className="text-sm text-[#F7F5F0]/60 mb-4">{t("footer.efaDesc")}</p>
            <ul className="space-y-3 text-sm text-[#F7F5F0]/70">
              <li><Link to="/echoes-from-above" className="hover:text-[#C5A059] transition-colors" data-testid="footer-link-echoes">{t("footer.efaProject")}</Link></li>
              <li>
                <a href="https://open.spotify.com/intl-it/artist/7ngeIzyK6qFXTxEo9UXSg9?si=NCt0BhrfTCefzGImsHEhpQ" target="_blank" rel="noopener noreferrer" data-testid="footer-link-spotify" className="inline-flex items-center gap-1.5 hover:text-[#C5A059] transition-colors">
                  Spotify <ArrowUpRight size={12} />
                </a>
              </li>
              <li>
                <a href="https://music.apple.com/it/artist/echoes-from-above/1894971984" target="_blank" rel="noopener noreferrer" data-testid="footer-link-apple-music" className="inline-flex items-center gap-1.5 hover:text-[#C5A059] transition-colors">
                  Apple Music <ArrowUpRight size={12} />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#F7F5F0]/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[#F7F5F0]/50" data-testid="footer-copyright">
            {t("footer.copyright")}
          </p>
          <FooterLang />
        </div>
      </div>
    </footer>
  );
};

const FooterLang = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center gap-2 text-[11px] tracking-[0.2em]" data-testid="footer-language" role="group" aria-label="Language / Lingua / Langue">
      {["it", "en", "fr"].map((l, i) => (
        <span key={l} className="flex items-center gap-2">
          {i > 0 && <span className="text-[#F7F5F0]/25">|</span>}
          <button
            onClick={() => setLang(l)}
            data-testid={`footer-lang-${l}`}
            aria-pressed={lang === l}
            className={`uppercase transition-colors hover:text-[#C5A059] ${lang === l ? "text-[#C5A059]" : "text-[#F7F5F0]/50"}`}
          >
            {l}
          </button>
        </span>
      ))}
    </div>
  );
};

export default Footer;
